<template>
  <div class="tcf-page" ref="tcfPageRoot" :style="pageStyle">
    <!-- ===== Toolbar ===== -->
    <v-toolbar color="primary" density="comfortable" class="text-white">
      <v-btn variant="text" prepend-icon="mdi-arrow-left" data-testid="tcf-back" @click="goBack">
        รายการฟังก์ชัน
      </v-btn>
      <v-toolbar-title class="tcf-title">
        แก้ไขฟังก์ชัน
        <code v-if="form.functionCode" class="tcf-fc" data-testid="tcf-title-code">{{ form.functionCode }}</code>
        <span v-if="form.name" class="tcf-sub">· {{ form.name }}</span>
        <span v-if="versionCode" class="tcf-sub">· {{ versionCode }}</span>
      </v-toolbar-title>
      <v-spacer />

      <v-chip
        v-if="isActiveVersion"
        size="small"
        color="white"
        variant="elevated"
        class="mr-2 tcf-text-primary"
        data-testid="tcf-version-active"
      >
        <v-icon start size="14">mdi-circle-slice-8</v-icon> ACTIVE
      </v-chip>

      <v-chip v-if="dirty" size="small" color="warning" variant="tonal" class="mr-2" data-testid="tcf-unsaved">
        <v-icon start size="14">mdi-circle-medium</v-icon> ยังไม่บันทึก
      </v-chip>

      <v-chip
        v-if="!loading && !loadError"
        size="small"
        :color="health.color"
        variant="tonal"
        class="mr-2"
        data-testid="tcf-health"
      >
        <v-icon start size="14">{{ health.icon }}</v-icon> {{ health.text }}
      </v-chip>

      <v-btn
        variant="text"
        prepend-icon="mdi-check-circle-outline"
        :loading="validating"
        :disabled="loading || !!loadError || !code.trim() || running"
        data-testid="tcf-validate"
        @click="doValidate"
      >ตรวจสอบโค้ด</v-btn>

      <v-btn
        color="white"
        variant="elevated"
        prepend-icon="mdi-content-save-outline"
        :disabled="!canSave"
        :loading="saving"
        class="tcf-text-primary ml-2"
        data-testid="tcf-save"
        @click="doSave"
      >บันทึก</v-btn>
    </v-toolbar>

    <div v-if="loadError" class="tcf-center">
      <v-icon size="42" color="error">mdi-alert-circle-outline</v-icon>
      <span class="mt-3 text-error">{{ loadError }}</span>
      <v-btn class="mt-4" variant="tonal" color="primary" data-testid="tcf-retry" @click="loadAll">ลองอีกครั้ง</v-btn>
    </div>
    <div v-else-if="loading" class="tcf-center">
      <v-progress-circular indeterminate color="primary" size="48" />
      <span class="mt-3 text-grey">กำลังโหลดฟังก์ชัน…</span>
    </div>

    <template v-else>
      <!-- ===== Function metadata ===== -->
      <div class="tcf-meta">
        <v-text-field
          v-model="form.functionCode"
          label="รหัสฟังก์ชัน (ชื่อคลาส) *"
          hint="ต้องเป็นชื่อคลาส C# ที่ถูกต้อง เช่น CalculateNetIncome"
          persistent-hint
          density="compact"
          variant="outlined"
          hide-details="auto"
          class="tcf-meta-code"
          data-testid="tcf-functioncode"
        />
        <v-text-field
          v-model="form.name"
          label="ชื่อที่แสดง *"
          hint="ชื่อที่คนอ่าน เช่น คำนวณเงินได้สุทธิ"
          persistent-hint
          density="compact"
          variant="outlined"
          hide-details="auto"
          class="tcf-meta-name"
          data-testid="tcf-name"
        />
        <!-- OUTPUT FieldKey — the mirror image of การผูกค่าพารามิเตอร์ below: that table says where this
             function READS its arguments from, this box says where its RESULT is written back to.

             v-combobox, deliberately NOT the v-autocomplete the binding table uses. The registry holds
             the fields the customer is ASKED (EXP_40_5_ACTUAL) and none of the DERIVED ones a formula
             produces (EXP_40_5) — a closed list would offer no valid destination at all. So: search the
             registry when the key is in it, type it when it is not. -->
        <v-combobox
          :model-value="form.fieldKey || null"
          :items="outputFieldItems"
          item-title="title"
          item-value="value"
          :custom-filter="filterFieldItem"
          :loading="loadingFieldRules"
          label="FieldKey ผลลัพธ์"
          placeholder="ค้นหา หรือพิมพ์คีย์ใหม่"
          :hint="outputFieldHint"
          persistent-hint
          density="compact"
          variant="outlined"
          hide-details="auto"
          clearable
          class="tcf-meta-field"
          data-testid="tcf-fieldkey"
          @update:model-value="setOutputFieldKey"
        >
          <template #item="{ props: itemProps, item }">
            <v-list-item v-bind="itemProps" :title="item.raw.fieldKey || item.title">
              <template #subtitle>
                <span class="tcf-bind-opt-label">{{ item.raw.label || '—' }}</span>
                <v-chip v-if="item.raw.valueType" size="x-small" variant="tonal" class="ml-1">
                  {{ item.raw.valueType }}
                </v-chip>
                <v-chip v-else-if="item.raw.missing" size="x-small" color="warning" variant="tonal" class="ml-1">
                  คีย์ใหม่
                </v-chip>
              </template>
            </v-list-item>
          </template>
        </v-combobox>
        <v-text-field
          v-model="form.description"
          label="คำอธิบาย"
          density="compact"
          variant="outlined"
          hide-details
          class="tcf-meta-desc"
          data-testid="tcf-description"
        />
        <v-text-field
          v-model.number="form.sortOrder"
          label="ลำดับ"
          type="number"
          density="compact"
          variant="outlined"
          hide-details
          class="tcf-meta-order"
          data-testid="tcf-sortorder"
        />
        <v-switch
          v-model="form.isActive"
          label="เปิดใช้งาน"
          color="primary"
          density="compact"
          hide-details
          class="tcf-meta-active"
          data-testid="tcf-isactive"
        />
      </div>

      <!-- A draft rescued from this browser. NEVER applied on its own: the person who opens a tax
           function must be told the code on screen is not what the server holds, and choose. Keyed by
           functionID — one global key let two functions overwrite each other's drafts. -->
      <v-alert
        v-if="draft"
        type="info"
        variant="tonal"
        density="compact"
        class="tcf-draft"
        data-testid="tcf-draft"
      >
        <div class="d-flex align-center flex-wrap ga-2">
          <span>พบฉบับร่างที่ยังไม่ได้บันทึกในเครื่องนี้ ({{ draftAgeText }}) — โค้ดต่างจากที่บันทึกไว้บนเซิร์ฟเวอร์</span>
          <v-spacer />
          <v-btn size="small" variant="tonal" color="primary" data-testid="tcf-draft-restore" @click="restoreDraft">
            ใช้ฉบับร่าง
          </v-btn>
          <v-btn size="small" variant="text" data-testid="tcf-draft-discard" @click="discardDraft">
            ทิ้งฉบับร่าง
          </v-btn>
        </div>
      </v-alert>

      <!-- ===== Editor (left) + Run panel (right) ===== -->
      <div class="tcf-body">
        <div class="tcf-editor-pane">
          <div class="tcf-pane-head">
            <v-icon size="16" color="primary" class="mr-1">mdi-language-csharp</v-icon>
            <span class="tcf-pane-title">โค้ด C#</span>
            <code class="tcf-entry" v-if="entryPointText">{{ entryPointText }}</code>
            <v-spacer />
            <span class="tcf-hint">
              <v-icon size="13">mdi-information-outline</v-icon>
              บันทึกได้เสมอ แม้โค้ดยังคอมไพล์ไม่ผ่าน — ระบบจะบันทึกสถานะคอมไพล์ไว้ที่ฟังก์ชันนี้
            </span>
          </div>

          <div v-if="classMismatch" class="tcf-classfix" data-testid="tcf-classmismatch">
            <v-icon size="14" color="warning">mdi-alert-outline</v-icon>
            <span>
              ชื่อคลาสในโค้ดคือ <code>{{ classMismatch }}</code> แต่รหัสฟังก์ชันคือ
              <code>{{ form.functionCode }}</code> — คอมไพล์จะหา entry point ไม่เจอ
            </span>
            <v-spacer />
            <v-btn size="x-small" variant="tonal" color="warning" data-testid="tcf-syncclass" @click="syncClassName">
              แก้ชื่อคลาสให้ตรง
            </v-btn>
          </div>

          <div class="tcf-editor-shell" ref="editorHost">
            <PrismEditor
              v-model="code"
              class="tcf-editor"
              :highlight="highlighter"
              :line-numbers="true"
              data-testid="tcf-code"
              aria-label="ตัวแก้ไขโค้ด C#"
            />
          </div>
        </div>

        <div class="tcf-run-pane" data-testid="tcf-run-pane">
          <div class="tcf-pane-head">
            <v-icon size="16" color="primary" class="mr-1">mdi-play-circle-outline</v-icon>
            <span class="tcf-pane-title">ทดลองรัน</span>
            <v-spacer />
            <v-chip v-if="paramsStale" size="x-small" color="warning" variant="tonal" data-testid="tcf-params-stale">
              โค้ดเปลี่ยนแล้ว — กด “ตรวจสอบโค้ด”
            </v-chip>
            <v-chip v-else size="x-small" variant="tonal" data-testid="tcf-params-source">
              {{ paramsSourceText }}
            </v-chip>
          </div>

          <div class="tcf-run-body">
            <!-- Entered values SURVIVE a re-validate: staleness is computed from the code buffer, never
                 written by a keystroke watcher, so nothing here is wiped while the author is typing. -->
            <div v-if="activeParameters.length" class="tcf-params">
              <div v-for="p in activeParameters" :key="p.name" class="tcf-param-wrap">
                <!-- A parameter bound to a FieldKey still shows an EDITABLE input: the caller's argument
                     wins over the resolver, so this box is the override, not a read-only mirror. -->
                <div
                  v-if="fieldBindingOf(p.name)"
                  class="tcf-param-bound"
                  :data-testid="`tcf-param-bound-${p.name}`"
                >
                  <v-icon size="12">mdi-link-variant</v-icon>
                  จาก <code>{{ fieldBindingOf(p.name) }}</code> — กรอกที่นี่เพื่อทับค่าที่ผูกไว้
                </div>
                <v-checkbox
                  v-if="p.type === 'Boolean'"
                  v-model="inputs[p.name]"
                  :label="paramLabel(p)"
                  :error="bindingParam === p.name"
                  :messages="paramHint(p)"
                  color="primary"
                  density="compact"
                  class="tcf-param"
                  :data-testid="`tcf-param-${p.name}`"
                />
                <v-text-field
                  v-else
                  v-model="inputs[p.name]"
                  :label="paramLabel(p)"
                  :type="paramInputType(p)"
                  :hint="paramHint(p)"
                  persistent-hint
                  :error="bindingParam === p.name"
                  density="compact"
                  variant="outlined"
                  class="tcf-param"
                  :data-testid="`tcf-param-${p.name}`"
                />
              </div>
            </div>
            <div v-else class="tcf-empty" data-testid="tcf-noparams">
              <v-icon size="15" class="mr-1">mdi-tune-variant</v-icon>
              ฟังก์ชันนี้ไม่ต้องการค่านำเข้า
            </div>

            <v-btn
              block
              color="primary"
              variant="flat"
              prepend-icon="mdi-play"
              class="mt-3"
              :loading="running"
              :disabled="validating || saving"
              data-testid="tcf-run"
              @click="doRun"
            >รันฟังก์ชัน</v-btn>

            <!-- errorKind: timeout — its own amber note, not an error list entry. The code compiled and
                 ran; it simply did not finish, and the fix is the algorithm, not a syntax error. -->
            <v-alert
              v-if="runResult && runResult.errorKind === 'timeout'"
              type="warning"
              variant="tonal"
              density="compact"
              class="mt-3"
              data-testid="tcf-timeout"
            >
              เกินเวลาที่อนุญาต — ฟังก์ชันรันไม่จบภายในเวลาที่กำหนด
              <div v-if="runResult.errorMessage" class="tcf-run-sub">{{ runResult.errorMessage }}</div>
            </v-alert>

            <!-- errorKind: binding — the offending input is highlighted above; say which one. -->
            <v-alert
              v-else-if="runResult && runResult.errorKind === 'binding'"
              type="error"
              variant="tonal"
              density="compact"
              class="mt-3"
              data-testid="tcf-binding-error"
            >
              ค่านำเข้าไม่ถูกต้อง<span v-if="runResult.errorParameter">: <code>{{ runResult.errorParameter }}</code></span>
              <div v-if="runResult.errorMessage" class="tcf-run-sub">{{ runResult.errorMessage }}</div>
            </v-alert>

            <!-- errorKind: compile — the diagnostics went to the persistent list below. -->
            <v-alert
              v-else-if="runResult && runResult.errorKind === 'compile'"
              type="error"
              variant="tonal"
              density="compact"
              class="mt-3"
              data-testid="tcf-compile-error"
            >
              คอมไพล์ไม่ผ่าน — ดูรายการข้อผิดพลาดด้านล่าง
            </v-alert>

            <!-- ok, or errorKind: runtime — both belong in the result pane. -->
            <div v-if="runResult && showResultPane" class="tcf-result" data-testid="tcf-result">
              <div class="tcf-result-head">
                <v-icon size="15" :color="runResult.ok ? 'success' : 'error'" class="mr-1">
                  {{ runResult.ok ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline' }}
                </v-icon>
                <strong>{{ runResult.ok ? 'ผลลัพธ์' : 'เกิดข้อผิดพลาดขณะรัน' }}</strong>
                <v-spacer />
                <span v-if="runResult.elapsedMs != null" class="tcf-elapsed">{{ runResult.elapsedMs }} ms</span>
              </div>
              <pre v-if="runResult.ok" class="tcf-result-pre" data-testid="tcf-result-value">{{ formattedResult }}</pre>
              <template v-else>
                <div class="tcf-run-sub" data-testid="tcf-runtime-message">
                  {{ runResult.errorMessage || 'รันไม่สำเร็จ' }}
                </div>
                <v-expansion-panels v-if="runResult.errorDetail" variant="accordion" class="mt-2">
                  <v-expansion-panel data-testid="tcf-runtime-detail">
                    <v-expansion-panel-title class="tcf-detail-title">รายละเอียด</v-expansion-panel-title>
                    <v-expansion-panel-text>
                      <pre class="tcf-result-pre">{{ runResult.errorDetail }}</pre>
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
              </template>
              <div class="tcf-result-meta">
                <span v-if="runResult.entryPoint"><code>{{ runResult.entryPoint }}</code></span>
                <span v-if="runResult.compiled === false" class="tcf-result-cached">ใช้ผลคอมไพล์ที่แคชไว้</span>
              </div>
            </div>

            <v-alert
              v-if="transportError"
              type="error"
              variant="tonal"
              density="compact"
              class="mt-3"
              data-testid="tcf-transport-error"
            >{{ transportError }}</v-alert>
          </div>
        </div>
      </div>

      <!-- ===== Parameter binding =====
           Where each parameter of Run(...) gets its value when the formula runs against a REAL form —
           the only place a formula stops being a hand-fed function. One row per parameter, one switch:
           on = read a Tax Config FieldKey, off = a constant typed here, off+blank = the C# default.
           Nothing here is auto-applied and nothing is auto-deleted: the author reviews and saves. -->
      <section class="tcf-bindings" data-testid="tcf-bindings">
        <div class="tcf-bind-head">
          <v-icon size="16" color="primary" class="mr-1">mdi-link-variant</v-icon>
          <span class="tcf-pane-title">การผูกค่าพารามิเตอร์</span>
          <span class="tcf-hint ml-2" data-testid="tcf-bind-summary">{{ bindingSummaryText }}</span>
          <v-chip
            v-if="bindingCounts.blocking"
            size="x-small"
            color="error"
            variant="tonal"
            class="ml-2"
            data-testid="tcf-bind-blocking"
          >
            ต้องผูกอีก {{ bindingCounts.blocking }}
          </v-chip>
          <v-chip
            v-if="paramsStale && activeParameters.length"
            size="x-small"
            color="warning"
            variant="tonal"
            class="ml-2"
            data-testid="tcf-bind-stale"
          >
            รายการพารามิเตอร์อาจเก่า — กด “ตรวจสอบโค้ด”
          </v-chip>
          <v-spacer />
          <v-btn
            size="small"
            variant="outlined"
            :disabled="!canGuessFieldKeys"
            :loading="loadingFieldRules"
            class="mr-1"
            data-testid="tcf-bind-guess"
            @click="guessFieldKeys"
          >
            <v-icon start>mdi-auto-fix</v-icon>เดา FieldKey จากชื่อพารามิเตอร์
          </v-btn>
          <v-btn
            size="small"
            variant="text"
            :icon="bindingsOpen ? 'mdi-chevron-down' : 'mdi-chevron-up'"
            :aria-label="bindingsOpen ? 'ย่อการผูกค่าพารามิเตอร์' : 'ขยายการผูกค่าพารามิเตอร์'"
            data-testid="tcf-bind-collapse"
            @click="bindingsOpen = !bindingsOpen"
          />
        </div>

        <div v-show="bindingsOpen" class="tcf-bind-body">
          <!-- A registry that failed to load NEVER clears a stored binding — the constant column still
               works, and the keys already chosen stay on screen exactly as saved. -->
          <v-alert
            v-if="fieldRulesError"
            type="warning"
            variant="tonal"
            density="compact"
            class="mb-2"
            data-testid="tcf-bind-registry-error"
          >{{ fieldRulesError }}</v-alert>

          <div
            v-for="p in activeParameters"
            :key="p.name"
            class="tcf-bind-row"
            :class="{ 'tcf-bind-row-error': bindingStatus(p).error }"
            :data-testid="`tcf-bind-row-${p.name}`"
          >
            <div class="tcf-bind-name">
              <code class="tcf-bind-code">{{ paramLabel(p) }}</code>
              <span class="tcf-bind-type">{{ paramHint(p) }}</span>
            </div>

            <div class="tcf-bind-input">
              <!-- v-combobox, NOT a closed v-autocomplete: besides the registry's FieldKeys a binding may be
                   `calc:<FieldKey>` (another formula's result — never in the registry) or a sub-total/fact key
                   (INCOME_40_5#BUILDING_30, INCOME_40_1@PERIOD). A closed list made those impossible to enter
                   from this page even though the backend resolver has always accepted them. The calc: results
                   of this calc version are offered as items; anything else can still be typed. -->
              <v-combobox
                v-if="rowOf(p.name).useField"
                :model-value="rowOf(p.name).fieldKey || null"
                :items="bindingFieldItems"
                item-title="title"
                item-value="value"
                :custom-filter="filterFieldItem"
                :loading="loadingFieldRules || loadingCalcOutputs"
                :no-data-text="fieldNoDataText"
                label="FieldKey จาก Tax Config / calc:ผลสูตร"
                placeholder="ค้นหา FieldKey/Label หรือพิมพ์ calc:KEY"
                prepend-inner-icon="mdi-magnify"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                :error="bindingStatus(p).missing"
                :data-testid="`tcf-bind-field-${p.name}`"
                @update:model-value="setBindingFieldKey(p.name, fieldKeyOf($event))"
              >
                <template #item="{ props: itemProps, item }">
                  <v-list-item v-bind="itemProps" :title="item.raw.fieldKey">
                    <template #subtitle>
                      <span class="tcf-bind-opt-label">{{ item.raw.label || '—' }}</span>
                      <v-chip v-if="item.raw.isCalc" size="x-small" color="primary" variant="tonal" class="ml-1">
                        ผลสูตร
                      </v-chip>
                      <v-chip v-else-if="item.raw.valueType" size="x-small" variant="tonal" class="ml-1">
                        {{ item.raw.valueType }}
                      </v-chip>
                      <v-chip v-else-if="item.raw.missing" size="x-small" color="error" variant="tonal" class="ml-1">
                        ไม่พบใน config version
                      </v-chip>
                    </template>
                  </v-list-item>
                </template>
              </v-combobox>

              <v-text-field
                v-else
                :model-value="rowOf(p.name).value"
                label="ค่าคงที่"
                :placeholder="constantPlaceholder(p)"
                persistent-placeholder
                variant="outlined"
                density="compact"
                hide-details
                clearable
                :data-testid="`tcf-bind-const-${p.name}`"
                @update:model-value="setBindingValue(p.name, $event)"
              />
            </div>

            <div class="tcf-bind-switch">
              <v-switch
                :model-value="rowOf(p.name).useField"
                label="ใช้ค่าจาก Tax Config"
                color="primary"
                density="compact"
                hide-details
                inset
                :data-testid="`tcf-bind-toggle-${p.name}`"
                @update:model-value="setBindingUseField(p.name, $event)"
              />
            </div>

            <div class="tcf-bind-msg">
              <span v-if="bindingStatus(p).error" class="tcf-bind-err" :data-testid="`tcf-bind-required-${p.name}`">
                <v-icon size="13">mdi-alert-circle-outline</v-icon> {{ bindingStatus(p).error }}
              </span>
              <span
                v-if="bindingStatus(p).missing"
                class="tcf-bind-err"
                :data-testid="`tcf-bind-missing-${p.name}`"
              >
                <v-icon size="13">mdi-link-variant-off</v-icon> ไม่พบ FieldKey นี้ใน config version
              </span>
              <span v-if="bindingStatus(p).warn" class="tcf-bind-warn" :data-testid="`tcf-bind-warn-${p.name}`">
                <v-icon size="13">mdi-alert-outline</v-icon> {{ bindingStatus(p).warn }}
              </span>
              <span v-if="bindingStatus(p).hint" class="tcf-bind-note">{{ bindingStatus(p).hint }}</span>
            </div>
          </div>

          <div v-if="!activeParameters.length" class="tcf-empty" data-testid="tcf-bind-noparams">
            <v-icon size="15" class="mr-1">mdi-tune-variant</v-icon>
            ฟังก์ชันนี้ไม่มีพารามิเตอร์ — ไม่ต้องผูกค่า (ถ้าเพิ่งแก้โค้ด กด “ตรวจสอบโค้ด” เพื่อดึงพารามิเตอร์ล่าสุด)
          </div>

          <!-- Orphans: the parameter is gone from the signature but the binding is NOT thrown away —
               a renamed-by-typo parameter comes back and picks its binding up again by name. -->
          <div v-if="orphanBindingNames.length" class="tcf-bind-orphans" data-testid="tcf-bind-orphans">
            <div class="tcf-bind-orphan-head">
              <v-icon size="13">mdi-help-rhombus-outline</v-icon>
              ไม่พบพารามิเตอร์ชื่อนี้ในสูตรแล้ว — ระบบเก็บไว้ให้ ไม่ได้ลบอัตโนมัติ (แก้ชื่อในโค้ดกลับ แล้วการผูกจะกลับมาเอง)
            </div>
            <div
              v-for="name in orphanBindingNames"
              :key="`orphan-${name}`"
              class="tcf-bind-row tcf-bind-row-orphan"
              :data-testid="`tcf-bind-orphan-${name}`"
            >
              <div class="tcf-bind-name">
                <code class="tcf-bind-code">{{ name }}</code>
                <span class="tcf-bind-type">ไม่มีในสูตรปัจจุบัน</span>
              </div>
              <div class="tcf-bind-input tcf-bind-orphan-val">{{ orphanText(name) }}</div>
              <div class="tcf-bind-switch">
                <v-btn
                  size="small"
                  variant="outlined"
                  color="error"
                  :data-testid="`tcf-bind-orphan-remove-${name}`"
                  @click="removeOrphanBinding(name)"
                >
                  <v-icon start>mdi-delete-outline</v-icon>ลบการผูกนี้
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ===== PERSISTENT error list =====
           Not a 3.2s snackbar. Same rationale as TaxFormulaBlockly's error list: the compile errors of a
           tax function are the one thing the author must still be able to read while they fix line 42 —
           an author who looked away when the toast went would be editing a function whose only report of
           what is wrong has already disappeared. It is replaced only by the next validate / save / run,
           never by a timer. -->
      <div v-if="hasProblems" class="tcf-error-list" data-testid="tcf-errors">
        <div class="tcf-error-head">
          <v-icon size="14" color="error">mdi-alert-circle-outline</v-icon>
          <span>ข้อผิดพลาด {{ metaErrors.length + errorList.length }} รายการ</span>
          <span v-if="errorSource" class="tcf-error-src">· {{ errorSource }}</span>
          <v-spacer />
          <span class="tcf-hint">คลิกที่บรรทัดเพื่อไปยังตำแหน่งในโค้ด</span>
        </div>
        <div v-for="(m, i) in metaErrors" :key="`m-${i}`" class="tcf-error-item" data-testid="tcf-meta-error">
          <v-icon size="13" color="error">mdi-form-textbox</v-icon>
          <span>{{ m }}</span>
        </div>
        <div
          v-for="(e, i) in errorList"
          :key="`e-${i}`"
          class="tcf-error-item tcf-error-click"
          :class="{ 'tcf-error-active': activeErrorLine && e.line === activeErrorLine }"
          :data-testid="`tcf-error-${i}`"
          @click="goToError(e)"
        >
          <v-icon size="13" color="error">mdi-code-tags</v-icon>
          <span>{{ errorText(e) }}</span>
        </div>
      </div>
    </template>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" location="bottom" timeout="3200">
      {{ snackbar.message }}
    </v-snackbar>

    <!-- unsaved-changes guard (layer 3 of 3: beforeunload · beforeRouteLeave · this dialog) -->
    <v-dialog v-model="showLeaveDialog" max-width="420" persistent>
      <v-card>
        <v-card-title>ยังไม่ได้บันทึก</v-card-title>
        <v-card-text>ออกจากหน้านี้แล้วโค้ดที่แก้ไว้จะหายไป ต้องการออกหรือไม่?</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" data-testid="tcf-leave-cancel" @click="cancelLeave">อยู่ต่อ</v-btn>
          <v-btn color="error" variant="tonal" data-testid="tcf-leave-confirm" @click="confirmLeave">
            ออกโดยไม่บันทึก
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ConfirmDeleteDialog ref="confirmDelete" />
  </div>
</template>

<script>
import { PrismEditor } from 'vue-prism-editor'
import 'vue-prism-editor/dist/prismeditor.min.css'
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog.vue'
import { highlight, languages } from 'prismjs/components/prism-core'
import 'prismjs/components/prism-clike'
import 'prismjs/components/prism-csharp'
// NO `prismjs/themes/*.css` import. A Prism theme is a GLOBAL stylesheet and this view is statically
// imported by the router — importing one here would restyle every other Prism editor in the app
// (ModelData.vue / SettingExternalConnector.vue load prism-okaidia). The token colours live in this
// component's scoped block instead, derived from the Vuetify theme so they follow light/dark.
import { taxCalculateV2Api } from '../services/taxCalculateV2Api'
import { taxConfigV2Api } from '../services/taxConfigV2Api'

const FUNCTION_CODE_RE = /^[A-Za-z_][A-Za-z0-9_]*$/
const NUMERIC_TYPE_RE = /^(Byte|SByte|Int16|UInt16|Int32|UInt32|Int64|UInt64|Single|Double|Decimal)$/
const CLASS_DECL_RE = /\bclass\s+([A-Za-z_][A-Za-z0-9_]*)/
const DRAFT_PREFIX = 'taxCalcFunctionV2Draft:'
const DRAFT_DEBOUNCE_MS = 500

/** Read-only stand-in so the template can render a row before its state object exists. Frozen on
 *  purpose: a render must never be the thing that creates binding state (that is `ensureRow`'s job). */
/** Prefix of a binding that reads another formula's RESULT — `TaxCalcArgumentResolverV2.CalcPrefix`. */
const CALC_PREFIX = 'calc:'
const EMPTY_BINDING_ROW = Object.freeze({ useField: false, fieldKey: '', value: '' })

export default {
  name: 'TaxCalcFunctionV2Edit',
  components: { PrismEditor, ConfirmDeleteDialog },
  data() {
    return {
      loading: true,
      loadError: '',
      saving: false,
      validating: false,
      running: false,

      configVersionId: this.$route.params.configVersionId,
      calcVersionId: Number(this.$route.params.calcVersionId),
      functionId: Number(this.$route.params.functionId),

      version: null,
      fn: null,

      form: {
        functionCode: '',
        name: '',
        description: '',
        fieldKey: '',
        sortOrder: 0,
        isActive: true,
      },
      code: '',

      // The last PERSISTED state — dirty is a comparison against these, so a round-trip back to the
      // saved text clears "ยังไม่บันทึก" instead of latching it on for the rest of the session.
      savedForm: null,
      savedSourceCode: '',

      // The stored compile verdict of the SAVED source (compileStatus + isStale are the row-health
      // signal; a C# body cannot be previewed inline the way V1 previewed `= expression`).
      compileStatus: 'Draft',
      compileErrorCount: 0,
      isStale: false,

      // The last validate/run/save diagnostics — persistent, replaced only by the next action.
      errorList: [],
      errorSource: '',
      activeErrorLine: 0,
      transportError: '',

      // The last SUCCESSFUL validate and the exact source string it described. Parameter staleness is
      // derived from this (see paramsStale) rather than wiped by a watcher on every keystroke.
      lastGoodValidate: null,
      lastGoodValidateSource: '',

      // ── Parameter binding (UI-ONLY state) ──
      // Keyed by parameter name and deliberately NOT payload-shaped: a row keeps BOTH the FieldKey and
      // the constant the author typed, so flipping the switch back and forth loses neither. `doSave`
      // converts it to the wire shape (`buildParameterBindings`) and nothing else sends it.
      // A row is only ever ADDED — removing one because its parameter vanished from the signature is
      // exactly the auto-delete the design forbids (§5); those surface as the orphan list instead.
      /** @type {Object<string, {useField: boolean, fieldKey: string, value: string}>} */
      bindings: {},
      /** The persisted bindings as a payload-shaped JSON string — the dirty-check baseline. */
      savedBindingsJson: '[]',
      bindingsOpen: true,
      /** TaxConfigRuleV2 registry of THIS config version: `{ fieldKey, label, valueType }`. */
      fieldRules: [],
      loadingFieldRules: false,
      fieldRulesError: '',
      /** Output FieldKeys of the OTHER formulas in this calc version — the `calc:` targets:
       *  `{ fieldKey, functionCode, name, sortOrder, isActive }`. Fails soft like fieldRules. */
      calcOutputs: [],
      loadingCalcOutputs: false,
      calcOutputsLoaded: false,

      inputs: {},
      runResult: null,
      // The source the last run actually compiled — a successful run is proof about THAT text, not
      // about whatever has been typed since.
      lastRunSource: '',
      bindingParam: '',

      draft: null,
      draftTimer: null,

      topPad: 48,
      pageHeight: null,
      snackbar: { show: false, color: 'success', message: '' },
      showLeaveDialog: false,
      pendingRoute: null,
    }
  },
  computed: {
    pageStyle() {
      return {
        paddingTop: this.topPad + 'px',
        height: this.pageHeight != null ? this.pageHeight + 'px' : 'calc(100vh - 64px)',
      }
    },
    versionCode() {
      return (this.version && this.version.versionCode) || ''
    },
    isActiveVersion() {
      return !!(this.version && this.version.isActive)
    },
    dirty() {
      if (!this.savedForm) return false
      if (this.code !== this.savedSourceCode) return true
      // Binding edits are part of the save body, so they must be part of the diff — otherwise flipping a
      // switch leaves บันทึก looking like there is nothing to save and the guards let the page go.
      if (this.bindingsSignature !== this.savedBindingsJson) return true
      const a = this.savedForm
      const b = this.form
      return (
        String(a.functionCode || '') !== String(b.functionCode || '') ||
        String(a.name || '') !== String(b.name || '') ||
        String(a.description || '') !== String(b.description || '') ||
        String(a.fieldKey || '') !== String(b.fieldKey || '') ||
        Number(a.sortOrder || 0) !== Number(b.sortOrder || 0) ||
        a.isActive !== b.isActive
      )
    },
    metaErrors() {
      const errs = []
      const fc = String(this.form.functionCode || '').trim()
      if (!fc) errs.push('ต้องระบุรหัสฟังก์ชัน')
      else if (!FUNCTION_CODE_RE.test(fc)) {
        errs.push('รหัสฟังก์ชันต้องเป็นชื่อคลาส C# ที่ถูกต้อง (A-Z a-z 0-9 _ และห้ามขึ้นต้นด้วยตัวเลข)')
      }
      if (!String(this.form.name || '').trim()) errs.push('ต้องตั้งชื่อที่แสดง')
      if (!String(this.code || '').trim()) errs.push('ต้องมีโค้ด C#')
      return errs
    },
    hasProblems() {
      return this.metaErrors.length > 0 || this.errorList.length > 0
    },
    /**
     * A compile error is NEVER a failed save — the backend records the outcome on the row instead — so
     * only the fields this page owns can block บันทึก. Staying enabled when nothing is dirty is
     * deliberate: re-saving an `isStale` function is how the author forces a fresh compile.
     */
    canSave() {
      return !this.saving && !this.loading && !this.loadError && this.metaErrors.length === 0
    },
    /** The class name the source actually declares — a rename that does not reach the code is silent. */
    classMismatch() {
      const m = CLASS_DECL_RE.exec(this.code || '')
      const declared = m ? m[1] : ''
      const fc = String(this.form.functionCode || '').trim()
      if (!declared || !fc) return ''
      return declared === fc ? '' : declared
    },
    /** validate's parameters when we have a fresh one, else the parameters stored on the function. */
    activeParameters() {
      const src =
        this.lastGoodValidate && Array.isArray(this.lastGoodValidate.parameters)
          ? this.lastGoodValidate.parameters
          : (this.fn && Array.isArray(this.fn.parameters) ? this.fn.parameters : [])
      return src.map((p) => this.normalizeParameter(p))
    },
    paramsSourceText() {
      return this.lastGoodValidate ? 'จากผลตรวจสอบล่าสุด' : 'จากพารามิเตอร์ที่บันทึกไว้'
    },

    // ── parameter binding ──
    /** The exact list `doSave` would send — also the dirty-check baseline. */
    bindingsSignature() {
      return JSON.stringify(this.buildParameterBindings())
    },
    /** Lower-cased FieldKey → the registry's own casing. Lookup is case-insensitive because the
     *  backend resolver matches that way; a case-only difference is NOT a missing key. */
    knownFieldKeyMap() {
      const map = new Map()
      this.fieldRules.forEach((r) => {
        if (r.fieldKey && !map.has(r.fieldKey.toLowerCase())) map.set(r.fieldKey.toLowerCase(), r.fieldKey)
      })
      return map
    },
    /** Registry rows keyed lower-case, for the ValueType compatibility note. */
    fieldRuleMap() {
      const map = new Map()
      this.fieldRules.forEach((r) => {
        if (r.fieldKey && !map.has(r.fieldKey.toLowerCase())) map.set(r.fieldKey.toLowerCase(), r)
      })
      return map
    },
    /** Only a registry we actually received can prove a key is gone — a failed/empty fetch must not. */
    registryReady() {
      return !this.loadingFieldRules && !this.fieldRulesError && this.fieldRules.length > 0
    },
    /** Set once the function list of this calc version actually loaded (empty is a valid answer). */
    calcOutputsReady() {
      return this.calcOutputsLoaded
    },
    /**
     * Autocomplete items. `title` carries FieldKey AND Label so the default filter searches both
     * (`filterFieldItem` keeps that honest even if the title format changes later).
     *
     * 🔴 A bound key the registry no longer has is APPENDED as its own item: the selection then still
     * renders, still round-trips through a save, and is flagged rather than silently dropped.
     */
    fieldItems() {
      const items = this.fieldRules.map((r) => ({
        title: `${r.fieldKey} ${r.label || ''}`.trim(),
        value: r.fieldKey,
        fieldKey: r.fieldKey,
        label: r.label,
        valueType: r.valueType,
        missing: false,
      }))
      const known = this.knownFieldKeyMap
      const added = new Set()
      Object.keys(this.bindings).forEach((name) => {
        const key = String(this.bindings[name].fieldKey || '').trim()
        const lower = key.toLowerCase()
        if (!key || known.has(lower) || added.has(lower)) return
        added.add(lower)
        items.push({ title: key, value: key, fieldKey: key, label: '', valueType: '', missing: this.isMissingFieldKey(key) })
      })
      return items
    },
    /**
     * The `calc:` items — one per formula in this calc version that writes an output FieldKey (this
     * function excluded: it cannot read its own result). `title` is the key ALONE, for the same reason as
     * `outputFieldItems`: a combobox copies the picked item's title into its text box.
     */
    calcOutputItems() {
      const seen = new Set()
      const items = []
      this.calcOutputs.forEach((f) => {
        const key = `${CALC_PREFIX}${f.fieldKey}`
        const lower = key.toLowerCase()
        if (seen.has(lower)) return
        seen.add(lower)
        const label = `${f.name || f.functionCode} · ลำดับ ${f.sortOrder}${f.isActive ? '' : ' (ปิดอยู่)'}`
        items.push({ title: key, value: key, fieldKey: key, label, valueType: 'number', missing: false, isCalc: true })
      })
      return items
    },
    /**
     * The binding combobox's items: registry FieldKeys + `calc:` results. A bound key in neither list is
     * still appended by `fieldItems` (flagged), so a saved binding always renders as the current choice.
     */
    bindingFieldItems() {
      const calc = this.calcOutputItems
      const calcKeys = new Set(calc.map((it) => it.value.toLowerCase()))
      const registry = this.fieldItems
        // a bound calc: key that a formula does produce is listed once, as the calc item
        .filter((it) => !(it.missing && calcKeys.has(String(it.value).toLowerCase())))
        .map((it) => ({ ...it, title: it.fieldKey || it.value }))
      return [...registry, ...calc]
    },
    fieldNoDataText() {
      if (this.loadingFieldRules) return 'กำลังโหลดรายการ FieldKey…'
      if (this.fieldRulesError) return 'โหลดรายการ FieldKey ไม่สำเร็จ'
      return 'ไม่พบ FieldKey ที่ค้นหา'
    },
    /**
     * The OUTPUT combobox's items — the SAME registry list `fieldItems` already built, with the two
     * differences a free-text combobox needs:
     *
     * 🔴 `title` is the FieldKey ALONE. VCombobox writes `item.title` straight into its own text box when
     * an item is picked, so the INPUT list's `"KEY ชื่อไทย"` title would leave "KEY ชื่อไทย" sitting in the
     * box — and the next Enter would commit that whole string as the key. Searching by label is NOT lost:
     * `filterFieldItem` reads `raw.label`, not the title.
     *
     * 🔴 The key already saved on this function is APPENDED when the registry has no such rule. `fieldItems`
     * only tops up keys used by `bindings` (the INPUT side), and a derived output key is normally absent
     * from the registry altogether — without its own item it would never appear in the list it is supposed
     * to be the current choice of.
     */
    outputFieldItems() {
      // calc: keys are READ targets (another formula's result) — never a place to write this one's
      const items = this.fieldItems
        .filter((it) => !this.isCalcKey(it.value))
        .map((it) => ({ ...it, title: it.fieldKey || it.value }))
      const key = String(this.form.fieldKey || '').trim()
      if (!key) return items
      const lower = key.toLowerCase()
      if (items.some((it) => String(it.fieldKey || '').toLowerCase() === lower)) return items
      items.push({ title: key, value: key, fieldKey: key, label: '', valueType: '', missing: true })
      return items
    },
    /** Is the OUTPUT key absent from the registry? Only a registry we actually received can say so. */
    outputFieldMissing() {
      return this.isMissingFieldKey(this.form.fieldKey)
    },
    /**
     * One line under the box. A key the registry does not have is NOT an error here — a formula that
     * computes something new is exactly the case the registry cannot contain — so it is stated, not flagged.
     */
    outputFieldHint() {
      if (this.loadingFieldRules) return 'กำลังโหลดรายการ FieldKey…'
      if (this.fieldRulesError) return 'พิมพ์คีย์เองได้ — โหลดรายการจาก Tax Config ไม่สำเร็จ'
      const key = String(this.form.fieldKey || '').trim()
      if (!key) return 'ปลายทางที่ผลลัพธ์จะถูกเขียน (ว่างได้)'
      if (this.outputFieldMissing) return 'คีย์ใหม่ — ยังไม่มีใน Tax Config version นี้'
      const rule = this.ruleFor(key)
      return rule && rule.label ? `เขียนผลลัพธ์ลง: ${rule.label}` : 'ปลายทางที่ผลลัพธ์จะถูกเขียน'
    },
    /** Bindings whose parameter is not in the current signature — kept, never auto-removed. */
    orphanBindingNames() {
      const active = new Set(this.activeParameters.map((p) => p.name))
      return Object.keys(this.bindings).filter(
        (name) => !active.has(name) && !!this.bindingEntry(name, this.bindings[name]),
      )
    },
    bindingCounts() {
      const counts = { field: 0, constant: 0, blank: 0, blocking: 0 }
      this.activeParameters.forEach((p) => {
        const entry = this.bindingEntry(p.name, this.rowOf(p.name))
        if (!entry) {
          counts.blank += 1
          if (p.required) counts.blocking += 1
        } else if (entry.source === 'FIELD') counts.field += 1
        else counts.constant += 1
      })
      return counts
    },
    bindingSummaryText() {
      if (!this.activeParameters.length) return 'ไม่มีพารามิเตอร์'
      const c = this.bindingCounts
      return `ผูกกับฟิลด์ ${c.field} · ค่าคงที่ ${c.constant} · ใช้ default ในสูตร ${c.blank}`
    },
    canGuessFieldKeys() {
      return !this.loadingFieldRules && this.fieldRules.length > 0 && this.activeParameters.length > 0
    },
    /**
     * Are the input fields describing an OLDER source than the buffer?
     *
     * Derived, not stored: the playground wiped `parameters` (and every value the author had typed)
     * from a watcher that fired on each keystroke. Computing staleness marks the panel instead, so the
     * values entered for a run survive editing the code and re-validating.
     */
    paramsStale() {
      const base = this.lastGoodValidate ? this.lastGoodValidateSource : this.savedSourceCode
      return String(this.code || '') !== String(base || '')
    },
    entryPointText() {
      const ep = this.lastGoodValidate && this.lastGoodValidate.entryPoint
      const rt = (this.lastGoodValidate && this.lastGoodValidate.returnType) || (this.fn && this.fn.returnType)
      if (!ep) return rt ? String(rt) : ''
      return rt ? `${ep} → ${rt}` : String(ep)
    },
    /** compileStatus + isStale ARE the health signal; a stale cache never renders as a green tick. */
    health() {
      if (this.lastGoodValidate && this.lastGoodValidateSource === this.code) {
        return { color: 'success', icon: 'mdi-check-circle-outline', text: 'ตรวจสอบผ่าน' }
      }
      if (this.runResult && this.runResult.ok && this.lastRunSource === this.code) {
        return { color: 'success', icon: 'mdi-check-circle-outline', text: 'รันผ่าน' }
      }
      if (this.code !== this.savedSourceCode) {
        return { color: 'warning', icon: 'mdi-progress-question', text: 'ยังไม่ได้ตรวจโค้ดล่าสุด' }
      }
      if (this.isStale) {
        return { color: 'warning', icon: 'mdi-refresh-circle', text: 'ต้องคอมไพล์ใหม่' }
      }
      if (this.compileStatus === 'Compiled') {
        return { color: 'success', icon: 'mdi-check-circle-outline', text: 'คอมไพล์ผ่าน' }
      }
      if (this.compileStatus === 'Error') {
        const n = this.compileErrorCount || this.errorList.length
        return { color: 'error', icon: 'mdi-alert-circle-outline', text: n ? `ผิดพลาด ${n}` : 'คอมไพล์ไม่ผ่าน' }
      }
      return { color: 'grey', icon: 'mdi-file-document-edit-outline', text: 'ฉบับร่าง' }
    },
    showResultPane() {
      if (!this.runResult) return false
      return this.runResult.ok || this.runResult.errorKind === 'runtime' || !this.runResult.errorKind
    },
    formattedResult() {
      const v = this.runResult ? this.runResult.result : null
      if (v === null || v === undefined) return '(null)'
      return typeof v === 'object' ? JSON.stringify(v, null, 2) : String(v)
    },
    draftAgeText() {
      const at = this.draft && this.draft.savedAt
      if (!at) return 'ไม่ทราบเวลา'
      const d = new Date(at)
      if (Number.isNaN(d.getTime())) return 'ไม่ทราบเวลา'
      return d.toLocaleString('th-TH')
    },
  },
  watch: {
    // Only ADDS the keys it is missing — never overwrites, never removes. A re-validate that renames one
    // parameter must not cost the author the five values they already typed for the others.
    activeParameters: {
      immediate: true,
      handler(list) {
        list.forEach((p) => {
          if (!Object.prototype.hasOwnProperty.call(this.inputs, p.name)) {
            this.inputs[p.name] = p.type === 'Boolean'
              ? (p.default === true || p.default === 'true')
              : (p.default === null || p.default === undefined ? '' : String(p.default))
          }
          // Same additive rule for the binding rows — seeding an empty one costs nothing (an empty row
          // produces no payload entry) while REMOVING one would throw away the author's work the moment
          // a typo in the signature made a parameter disappear.
          if (!Object.prototype.hasOwnProperty.call(this.bindings, p.name)) {
            this.bindings[p.name] = { useField: false, fieldKey: '', value: '' }
          }
        })
      },
    },
    code() {
      this.activeErrorLine = 0
      this.scheduleDraftSave()
      this.$nextTick(() => this.paintErrorLines())
    },
    form: {
      deep: true,
      handler() { this.scheduleDraftSave() },
    },
    errorList() {
      this.$nextTick(() => this.paintErrorLines())
    },
  },
  mounted() {
    this._beforeUnload = (e) => {
      if (this.dirty) { e.preventDefault(); e.returnValue = '' }
    }
    window.addEventListener('beforeunload', this._beforeUnload)
    this._onResize = () => this.updateLayoutInsets()
    window.addEventListener('resize', this._onResize)
    this.$nextTick(() => this.updateLayoutInsets())
    this.loadAll()
  },
  beforeUnmount() {
    if (this._beforeUnload) window.removeEventListener('beforeunload', this._beforeUnload)
    if (this._onResize) window.removeEventListener('resize', this._onResize)
    if (this.draftTimer) { clearTimeout(this.draftTimer); this.draftTimer = null }
  },
  beforeRouteLeave(to, from, next) {
    if (this.dirty) {
      this.pendingRoute = next
      this.showLeaveDialog = true
    } else {
      next()
    }
  },
  methods: {
    // ── editor ──
    highlighter(code) {
      return highlight(code, languages.csharp || languages.clike, 'csharp')
    },

    // ── load ──
    async loadAll() {
      this.loading = true
      this.loadError = ''
      try {
        if (!Number.isFinite(this.functionId)) throw new Error('ไม่พบฟังก์ชันนี้ (id ไม่ถูกต้อง)')
        const [fn, version] = await Promise.all([
          taxCalculateV2Api.getFunction(this.functionId),
          taxCalculateV2Api.getVersion(this.calcVersionId).catch(() => null),
        ])
        if (!fn) throw new Error('ไม่พบฟังก์ชันนี้')
        this.version = version || null
        this.applyFunction(fn, true)
        this.checkDraft()
        // Not awaited: the FieldKey registry is an aid to the binding table, not a precondition for
        // opening the editor. It fails soft — see loadFieldRules.
        this.loadFieldRules()
        this.loadCalcOutputs()
      } catch (err) {
        this.loadError = (err && err.message) ? err.message : 'โหลดข้อมูลไม่สำเร็จ'
      } finally {
        this.loading = false
        this.$nextTick(() => {
          this.updateLayoutInsets()
          this.paintErrorLines()
        })
      }
    },
    /**
     * Adopt a FunctionFull/FunctionSummary as the persisted state.
     *
     * `adoptSource` is false for a save response that carries no sourceCode (a summary) — the editor
     * buffer is then still the truth about what was sent, and overwriting it with '' would erase the
     * author's function.
     */
    applyFunction(fn, adoptSource) {
      this.fn = fn
      this.form = {
        functionCode: fn.functionCode || '',
        name: fn.name || '',
        description: fn.description || '',
        fieldKey: fn.fieldKey || '',
        sortOrder: Number(fn.sortOrder) || 0,
        isActive: fn.isActive !== false,
      }
      if (adoptSource || typeof fn.sourceCode === 'string') {
        this.code = typeof fn.sourceCode === 'string' ? fn.sourceCode : this.code
      }
      // An ABSENT `parameterBindings` means "this response does not carry them" — the save response of a
      // backend that has not started returning the field yet looks exactly like that, and adopting it
      // would wipe the table the author just filled in. Only an array replaces local state; otherwise the
      // current state simply becomes the new baseline (what was sent is what is stored).
      if (Array.isArray(fn.parameterBindings)) {
        this.bindings = this.toBindingRows(fn.parameterBindings)
      }
      this.savedForm = { ...this.form }
      this.savedSourceCode = this.code
      this.savedBindingsJson = this.bindingsSignature
      this.compileStatus = fn.compileStatus || 'Draft'
      this.compileErrorCount = Number(fn.compileErrorCount) || (Array.isArray(fn.compileErrors) ? fn.compileErrors.length : 0)
      this.isStale = !!fn.isStale
      this.setErrors(fn.compileErrors, 'สถานะคอมไพล์ที่บันทึกไว้')
    },

    // ── local draft (keyed by functionID — one global key let two functions clobber each other) ──
    draftKey() {
      return `${DRAFT_PREFIX}${this.functionId}`
    },
    scheduleDraftSave() {
      if (this.loading || this.loadError) return
      if (this.draftTimer) clearTimeout(this.draftTimer)
      this.draftTimer = setTimeout(() => {
        this.draftTimer = null
        this.writeDraft()
      }, DRAFT_DEBOUNCE_MS)
    },
    writeDraft() {
      try {
        if (!this.dirty) {
          // An unresolved draft banner OWNS the key until the author picks ใช้/ทิ้ง. Without this the
          // load-time watcher flush (form replaced by applyFunction, dirty back to false) would delete
          // the very draft the banner is offering, and a reload would lose it for good.
          if (this.draft) return
          window.localStorage.removeItem(this.draftKey())
          return
        }
        window.localStorage.setItem(this.draftKey(), JSON.stringify({
          sourceCode: this.code,
          form: { ...this.form },
          // Stored in the WIRE shape, so restoring reuses the same normalizer the server response does.
          parameterBindings: this.buildParameterBindings(),
          savedAt: new Date().toISOString(),
        }))
      } catch (_e) { /* storage full / disabled — the draft is a convenience, never a requirement */ }
    },
    checkDraft() {
      this.draft = null
      let raw = null
      try { raw = window.localStorage.getItem(this.draftKey()) } catch (_e) { return }
      if (!raw) return
      try {
        const d = JSON.parse(raw)
        if (!d || typeof d.sourceCode !== 'string') throw new Error('bad draft')
        if (d.sourceCode === this.savedSourceCode) {
          window.localStorage.removeItem(this.draftKey())
          return
        }
        this.draft = d
      } catch (_e) {
        try { window.localStorage.removeItem(this.draftKey()) } catch (_e2) { /* ignore */ }
      }
    },
    restoreDraft() {
      const d = this.draft
      if (!d) return
      this.code = d.sourceCode
      if (d.form && typeof d.form === 'object') {
        this.form = {
          functionCode: d.form.functionCode || this.form.functionCode,
          name: d.form.name || this.form.name,
          description: d.form.description || '',
          // Through the same normalizer as the combobox: a draft is unvalidated data out of localStorage,
          // and `form.fieldKey` must be a string no matter what is sitting in that key.
          fieldKey: this.fieldKeyOf(d.form.fieldKey),
          sortOrder: Number(d.form.sortOrder) || 0,
          isActive: d.form.isActive !== false,
        }
      }
      if (Array.isArray(d.parameterBindings)) this.bindings = this.toBindingRows(d.parameterBindings)
      this.draft = null
      this.showSnack('ใช้ฉบับร่างในเครื่องแล้ว — ยังไม่ได้บันทึกขึ้นเซิร์ฟเวอร์', 'info')
    },
    discardDraft() {
      this.draft = null
      try { window.localStorage.removeItem(this.draftKey()) } catch (_e) { /* ignore */ }
    },

    // ── errors ──
    setErrors(list, source) {
      const arr = Array.isArray(list) ? list : []
      this.errorList = arr.map((e) => ({
        message: (e && (e.message || e.Message)) || 'ไม่ทราบสาเหตุ',
        line: Number(e && (e.line ?? e.Line)) || 0,
        column: Number(e && (e.column ?? e.Column)) || 0,
        id: (e && (e.id || e.Id || e.ID)) || '',
      }))
      this.errorSource = arr.length ? (source || '') : ''
      this.activeErrorLine = 0
    },
    errorText(e) {
      const where = e.line ? `บรรทัด ${e.line}:${e.column || 1} — ` : ''
      const id = e.id ? ` (${e.id})` : ''
      return `${where}${e.message}${id}`
    },
    /**
     * Jump to (and highlight) the line an error points at, with no extra dependency: the textarea's own
     * selection IS the highlight, the gutter cell gets a class, and the wrapper — which is the element
     * vue-prism-editor scrolls — is scrolled by hand so the line lands a third of the way down.
     */
    goToError(e) {
      const line = Number(e && e.line)
      if (!Number.isFinite(line) || line < 1) return
      this.activeErrorLine = line
      const host = this.$refs.editorHost
      if (!host) return
      const wrapper = host.querySelector('.prism-editor-wrapper')
      const ta = host.querySelector('textarea.prism-editor__textarea')
      const pre = host.querySelector('pre.prism-editor__editor')
      const lines = String(this.code || '').split('\n')
      const idx = Math.min(line, lines.length) - 1
      if (ta && idx >= 0) {
        let start = 0
        for (let i = 0; i < idx; i += 1) start += lines[i].length + 1
        const col = Math.max(1, Number(e.column) || 1)
        const from = start + Math.min(col - 1, lines[idx].length)
        try {
          ta.focus({ preventScroll: true })
          ta.setSelectionRange(from, start + lines[idx].length)
        } catch (_err) { /* older browser — the scroll below still lands them there */ }
      }
      if (wrapper && pre) {
        const lh = parseFloat(window.getComputedStyle(pre).lineHeight) || 20
        wrapper.scrollTop = Math.max(0, (line - 1) * lh - wrapper.clientHeight / 3)
      }
      this.paintErrorLines()
    },
    /** Mark every error line in the gutter (and the clicked one more strongly). */
    paintErrorLines() {
      const host = this.$refs.editorHost
      if (!host) return
      const gutter = host.querySelector('.prism-editor__line-numbers')
      if (!gutter) return
      const cells = gutter.querySelectorAll('.prism-editor__line-number')
      const errLines = new Set(this.errorList.map((e) => Number(e.line)).filter((n) => n > 0))
      cells.forEach((cell, i) => {
        const n = i + 1
        cell.classList.toggle('tcf-line-err', errLines.has(n))
        cell.classList.toggle('tcf-line-hit', this.activeErrorLine === n)
      })
    },

    // ── parameters ──
    normalizeParameter(p) {
      const name = (p && (p.name ?? p.Name)) || ''
      const type = String((p && (p.type ?? p.Type)) || 'String').split('.').pop()
      const required = !!(p && (p.required ?? p.Required))
      const def = p ? (p.default ?? p.Default ?? null) : null
      return { name: String(name), type, required, default: def }
    },
    paramLabel(p) {
      return `${p.name}${p.required ? ' *' : ''}`
    },
    paramHint(p) {
      if (p.default === null || p.default === undefined) return p.type
      return `${p.type} · ค่าเริ่มต้น ${p.default}`
    },
    paramInputType(p) {
      return NUMERIC_TYPE_RE.test(p.type) ? 'number' : 'text'
    },
    /**
     * Build the `arguments` bag. An OPTIONAL parameter left blank is OMITTED, not sent as null: the
     * backend fills it from the C# default, which is exactly what `required: false` means here.
     * A blank REQUIRED one is reported the same way the backend's binding error is — on the field.
     */
    buildArguments() {
      const out = {}
      for (const p of this.activeParameters) {
        const raw = this.inputs[p.name]
        const blank = raw === '' || raw === null || raw === undefined
        if (p.type === 'Boolean') {
          if (blank && !p.required) continue
          out[p.name] = raw === true || raw === 'true' || raw === 1 || raw === '1'
          continue
        }
        if (blank) {
          if (p.required) {
            const err = new Error(`ต้องระบุค่าให้พารามิเตอร์ “${p.name}”`)
            err.parameter = p.name
            throw err
          }
          continue
        }
        if (NUMERIC_TYPE_RE.test(p.type)) {
          const n = Number(raw)
          if (!Number.isFinite(n)) {
            const err = new Error(`พารามิเตอร์ “${p.name}” ต้องเป็นตัวเลข (${p.type})`)
            err.parameter = p.name
            throw err
          }
          out[p.name] = n
          continue
        }
        out[p.name] = String(raw)
      }
      return out
    },

    // ── parameter binding ──
    /**
     * The FieldKey registry of THIS config version (route param), same call the question-item dialog
     * makes. Fails soft on every failure mode: a missing configVersionId, a 4xx/5xx, an empty registry —
     * none of them may break the editor, clear a stored binding, or stop the constant column working.
     */
    async loadFieldRules() {
      const cid = this.configVersionId
      if (cid === null || cid === undefined || String(cid).trim() === '') {
        this.fieldRules = []
        this.fieldRulesError = 'ไม่ทราบ Tax Config version ของหน้านี้ — เลือก FieldKey ไม่ได้ (ใส่ค่าคงที่แทนได้ และการผูกที่บันทึกไว้ยังอยู่ครบ)'
        return
      }
      this.loadingFieldRules = true
      this.fieldRulesError = ''
      try {
        const rules = await taxConfigV2Api.getRules(cid, null, true)
        this.fieldRules = (rules || [])
          .map((r) => ({
            fieldKey: String((r && (r.fieldKey ?? r.FieldKey)) || '').trim(),
            label: String((r && (r.label ?? r.Label)) || ''),
            valueType: String((r && (r.valueType ?? r.ValueType)) || '').trim().toLowerCase(),
          }))
          .filter((r) => r.fieldKey)
      } catch (err) {
        this.fieldRules = []
        this.fieldRulesError = `โหลดรายการ FieldKey ไม่สำเร็จ (${(err && err.message) || 'ไม่ทราบสาเหตุ'}) — การผูกที่บันทึกไว้ยังอยู่ครบ ยังไม่ถูกลบ`
      } finally {
        this.loadingFieldRules = false
      }
    },
    /**
     * The `calc:` targets: every OTHER formula of this calc version that writes an output FieldKey.
     * Fails soft — a failed list only loses the suggestions; typing `calc:KEY` still works.
     */
    async loadCalcOutputs() {
      this.loadingCalcOutputs = true
      try {
        const list = await taxCalculateV2Api.listFunctions(this.calcVersionId, true)
        this.calcOutputs = (Array.isArray(list) ? list : [])
          .map((f) => ({
            functionId: Number(f && (f.functionID ?? f.functionId ?? f.FunctionID)),
            functionCode: String((f && (f.functionCode ?? f.FunctionCode)) || ''),
            name: String((f && (f.name ?? f.Name)) || ''),
            fieldKey: String((f && (f.fieldKey ?? f.FieldKey)) || '').trim(),
            sortOrder: Number(f && (f.sortOrder ?? f.SortOrder)) || 0,
            isActive: !!(f && (f.isActive ?? f.IsActive)),
          }))
          .filter((f) => f.fieldKey && f.functionId !== this.functionId)
          .sort((a, b) => a.sortOrder - b.sortOrder || a.fieldKey.localeCompare(b.fieldKey))
        this.calcOutputsLoaded = true
      } catch (err) {
        this.calcOutputs = []
        this.calcOutputsLoaded = false
      } finally {
        this.loadingCalcOutputs = false
      }
    },
    /** READ-ONLY row accessor for the template — never creates state during a render. */
    rowOf(name) {
      return this.bindings[name] || EMPTY_BINDING_ROW
    },
    /** Mutating accessor for event handlers. */
    ensureRow(name) {
      if (!this.bindings[name]) this.bindings[name] = { useField: false, fieldKey: '', value: '' }
      return this.bindings[name]
    },
    /**
     * Flip "ใช้ค่าจาก Tax Config". BOTH sides of the row are kept — turning the switch off does not
     * forget which FieldKey was chosen, so an accidental flip is one click to undo.
     */
    setBindingUseField(name, on) {
      this.ensureRow(name).useField = !!on
      this.scheduleDraftSave()
    },
    setBindingFieldKey(name, key) {
      this.ensureRow(name).fieldKey = key === null || key === undefined ? '' : String(key)
      this.scheduleDraftSave()
    },
    setBindingValue(name, value) {
      this.ensureRow(name).value = value === null || value === undefined ? '' : String(value)
      this.scheduleDraftSave()
    },
    /** Wire shape → UI rows. Tolerates PascalCase and a `value` of any primitive type. */
    toBindingRows(list) {
      const rows = {}
      ;(Array.isArray(list) ? list : []).forEach((b) => {
        const name = String((b && (b.name ?? b.Name)) || '').trim()
        if (!name) return
        const source = String((b && (b.source ?? b.Source)) || '').trim().toUpperCase()
        const value = b ? (b.value ?? b.Value) : null
        rows[name] = {
          useField: source === 'FIELD',
          fieldKey: String((b && (b.fieldKey ?? b.FieldKey)) || '').trim(),
          value: value === null || value === undefined ? '' : String(value),
        }
      })
      return rows
    },
    /**
     * One row → its payload entry, or null when the row says "use the default in the source".
     * The switch alone is not a binding: on-but-nothing-picked stores NOTHING (and the row says so),
     * because a half-finished FIELD entry would resolve to a missing argument at run time.
     */
    bindingEntry(name, row) {
      if (!row) return null
      if (row.useField) {
        const key = String(row.fieldKey || '').trim()
        return key ? { name, source: 'FIELD', fieldKey: key } : null
      }
      // Trimmed: a constant is handed to a C# type converter, where a stray space is a parse failure.
      const value = String(row.value === null || row.value === undefined ? '' : row.value).trim()
      return value === '' ? null : { name, source: 'CONSTANT', value }
    },
    /**
     * UI → wire. Signature order first, then the orphans VERBATIM: a save that dropped them would be
     * the auto-delete §5 forbids, and the author would lose a binding by doing nothing but saving.
     */
    buildParameterBindings() {
      const out = []
      const seen = new Set()
      this.activeParameters.forEach((p) => {
        seen.add(p.name)
        const entry = this.bindingEntry(p.name, this.bindings[p.name])
        if (entry) out.push(entry)
      })
      Object.keys(this.bindings).forEach((name) => {
        if (seen.has(name)) return
        const entry = this.bindingEntry(name, this.bindings[name])
        if (entry) out.push(entry)
      })
      return out
    },
    ruleFor(fieldKey) {
      const key = this.registryBaseKey(fieldKey).toLowerCase()
      return key ? (this.fieldRuleMap.get(key) || null) : null
    },
    /** Is a chosen key absent from the registry? Only answerable when a registry actually loaded. */
    isMissingFieldKey(fieldKey) {
      const key = String(fieldKey || '').trim()
      if (!key || this.isCalcKey(key) || !this.registryReady) return false
      return !this.knownFieldKeyMap.has(this.registryBaseKey(key).toLowerCase())
    },
    /** `calc:<FieldKey>` — reads a formula's result, never a registry field (see calcBindingNote). */
    isCalcKey(fieldKey) {
      return String(fieldKey || '').trim().toLowerCase().startsWith(CALC_PREFIX)
    },
    /**
     * The registry half of a composite key, mirroring the backend's validate: a sub-total
     * (`INCOME_40_5#BUILDING_30`) or fact (`INCOME_40_1@PERIOD`) key is checked on its base field.
     */
    registryBaseKey(fieldKey) {
      return String(fieldKey || '').trim().split(/[#@]/)[0]
    },
    /**
     * What a `calc:` binding row should say. Never an error — the backend accepts any calc: key and the
     * resolver reports a missing value at run time — but the two ways it reads nothing are worth a warning:
     * no formula writes that key, or the formula that does runs AFTER this one (ordering = SortOrder, and a
     * key not yet computed leaves the parameter at its default).
     */
    calcBindingNote(fieldKey) {
      const target = String(fieldKey || '').trim().slice(CALC_PREFIX.length).trim()
      if (!target) return { warn: `ใส่ FieldKey ของผลสูตรต่อท้าย ${CALC_PREFIX} ด้วย เช่น ${CALC_PREFIX}INCOME_TOTAL_NET` }
      if (this.loadingCalcOutputs || !this.calcOutputsReady) return { hint: 'อ่านผลจากสูตรอื่น' }
      const producers = this.calcOutputs.filter((f) => f.fieldKey.toLowerCase() === target.toLowerCase())
      if (!producers.length) {
        return { warn: `ยังไม่มีสูตรไหนใน calc version นี้เขียน FieldKey '${target}' — พารามิเตอร์จะได้ค่า default` }
      }
      const mine = Number(this.form.sortOrder) || 0
      const before = producers.filter((f) => f.isActive && f.sortOrder < mine)
      const names = producers.map((f) => `${f.functionCode} (ลำดับ ${f.sortOrder})`).join(', ')
      if (!before.length) {
        return { warn: `สูตรที่เขียน '${target}' คือ ${names} — ไม่มีตัวที่เปิดอยู่และรันก่อนสูตรนี้ (ลำดับ ${mine}) ⇒ รอบแรกจะได้ค่า default` }
      }
      return { hint: `ผลสูตร ${names}` }
    },
    /** The ValueType a C# parameter would like. '' = do not guess (DateTime, custom types, …). */
    expectedValueType(p) {
      if (p.type === 'Boolean') return 'boolean'
      if (NUMERIC_TYPE_RE.test(p.type)) return 'number'
      if (p.type === 'String') return 'text'
      return ''
    },
    constantPlaceholder(p) {
      return p.default === null || p.default === undefined ? 'ว่างไว้ = ไม่ส่งค่านี้' : String(p.default)
    },
    /**
     * Everything a row has to say, in one pass: which entry it produces, what blocks the run, and what
     * merely deserves a warning.
     *
     * 🔴 A type mismatch is a NOTE, never a filter. ValueType in the registry is not trustworthy enough
     * to hard-filter on (real `boolean` fields store `FULLYEAR`), so a filtered dropdown would hide the
     * correct choice; the author is told and decides.
     */
    bindingStatus(p) {
      const row = this.rowOf(p.name)
      const entry = this.bindingEntry(p.name, row)
      const status = { entry, error: '', warn: '', hint: '', missing: false }
      if (!entry && p.required) {
        status.error = 'สูตรนี้จะรันไม่ได้ — ต้องผูกค่าหรือใส่ค่าคงที่'
      }
      if (row.useField) {
        status.missing = this.isMissingFieldKey(row.fieldKey)
        if (!String(row.fieldKey || '').trim()) {
          status.warn = 'เปิดใช้ค่าจาก Tax Config แล้ว แต่ยังไม่ได้เลือก FieldKey — ยังไม่ถูกบันทึกเป็นการผูกค่า'
        } else if (this.isCalcKey(row.fieldKey)) {
          Object.assign(status, this.calcBindingNote(row.fieldKey))
        } else {
          const rule = this.ruleFor(row.fieldKey)
          const want = this.expectedValueType(p)
          if (rule && rule.valueType && want && rule.valueType !== want) {
            status.warn = `ชนิดอาจไม่ตรงกัน — พารามิเตอร์เป็น ${p.type} แต่ฟิลด์นี้ประกาศเป็น ${rule.valueType} (ไม่บล็อกให้ ตรวจข้อมูลจริงก่อน)`
          } else if (rule && rule.label) {
            status.hint = rule.label
          }
        }
      } else if (!entry && !p.required) {
        status.hint = `ว่างไว้ = ใช้ default ในสูตร${p.default === null || p.default === undefined ? '' : ` (${p.default})`}`
      }
      return status
    },
    /** What an orphan row holds, in one readable line. */
    orphanText(name) {
      const entry = this.bindingEntry(name, this.bindings[name])
      if (!entry) return ''
      return entry.source === 'FIELD' ? `ผูกกับฟิลด์ ${entry.fieldKey}` : `ค่าคงที่ ${entry.value}`
    },
    async removeOrphanBinding(name) {
      const ok = await this.$refs.confirmDelete.ask({
        message: 'ต้องการลบการผูก FieldKey ของพารามิเตอร์นี้ใช่หรือไม่?',
        target: name,
      })
      if (!ok) return
      delete this.bindings[name]
      this.scheduleDraftSave()
      this.showSnack(`ลบการผูกของ “${name}” แล้ว — กดบันทึกเพื่อยืนยัน`, 'info')
    },
    /**
     * Fill in the obvious ones: a parameter with NO binding yet whose name matches a FieldKey
     * case-insensitively. It never overwrites an existing binding and it never saves — the author reads
     * the result and presses บันทึก, so a wrong guess costs nothing.
     */
    guessFieldKeys() {
      if (!this.fieldRules.length) {
        this.showSnack('ยังไม่มีรายการ FieldKey ให้เดา', 'warning')
        return
      }
      let filled = 0
      this.activeParameters.forEach((p) => {
        const row = this.ensureRow(p.name)
        if (this.bindingEntry(p.name, row)) return
        const hit = this.knownFieldKeyMap.get(String(p.name).toLowerCase())
        if (!hit) return
        row.useField = true
        row.fieldKey = hit
        filled += 1
      })
      if (filled) {
        this.bindingsOpen = true
        this.scheduleDraftSave()
        this.showSnack(`เดาให้ ${filled} พารามิเตอร์ — ตรวจแล้วกดบันทึกเอง`, 'success')
      } else {
        this.showSnack('ไม่พบ FieldKey ที่ชื่อตรงกับพารามิเตอร์ที่ยังไม่ได้ผูก', 'info')
      }
    },
    /** The FieldKey a run-panel input is bound to (''=none) — the badge next to the override box. */
    fieldBindingOf(name) {
      const row = this.bindings[name]
      if (!row || !row.useField) return ''
      return String(row.fieldKey || '').trim()
    },
    /** Search BOTH the key and the Thai label: admins remember "เงินได้ 40(1)", not INCOME_40_1. */
    filterFieldItem(_value, query, item) {
      const q = String(query || '').trim().toLowerCase()
      if (!q) return true
      const raw = (item && item.raw) || {}
      return `${raw.fieldKey || ''} ${raw.label || ''}`.toLowerCase().includes(q)
    },

    // ── output FieldKey (form.fieldKey) ──
    /**
     * 🔴 The ONE place a combobox value becomes `form.fieldKey`.
     *
     * VCombobox ships with `return-object: true`, so what it emits depends on how the author entered the
     * key: the ITEM OBJECT when they pick one from the list, a plain STRING when they type one, `null`
     * when they clear it. Everything downstream assumes a string — the dirty-check compares
     * `String(a.fieldKey) !== String(b.fieldKey)` (an object stringifies to "[object Object]", so the page
     * would read as unsaved forever and the leave guards would never let go), the save payload, and the
     * localStorage draft. The shape is flattened here so none of them ever sees anything else.
     *
     * Deliberately NOT trimmed and NOT upper-cased: a FieldKey is a verbatim runtime key, and trimming on
     * every keystroke fights the text box (VCombobox copies the model back into its own search text). The
     * trim happens once, at the API boundary in `doSave`.
     */
    setOutputFieldKey(v) {
      this.form.fieldKey = this.fieldKeyOf(v)
    },
    /** string | item object | ListItem | null → the plain FieldKey string. */
    fieldKeyOf(v) {
      if (v === null || v === undefined) return ''
      if (typeof v !== 'object') return String(v)
      const raw = (v.raw && typeof v.raw === 'object') ? v.raw : v
      const key = raw.fieldKey ?? raw.value ?? raw.title ?? ''
      return key === null || key === undefined ? '' : String(key)
    },

    // ── validate ──
    /**
     * validate and run must describe the SAME text. The playground sent `code.trim()` to validate and
     * the untrimmed buffer to run — two different sources, two different compile-cache entries, and a
     * "validated" function whose run compiled something else.
     */
    editorSource() {
      return this.code
    },
    async doValidate() {
      if (this.validating) return
      this.validating = true
      this.transportError = ''
      this.bindingParam = ''
      try {
        const res = await taxCalculateV2Api.validateFunction(this.calcVersionId, {
          functionCode: String(this.form.functionCode || '').trim(),
          sourceCode: this.editorSource(),
        })
        const ok = !!(res && res.ok)
        this.setErrors(res && res.errors, 'ผลการตรวจสอบโค้ด')
        if (ok) {
          this.lastGoodValidate = res
          this.lastGoodValidateSource = this.editorSource()
          this.runResult = null
          this.showSnack('ตรวจสอบผ่าน — ป้อนค่านำเข้าแล้วกดรันได้เลย', 'success')
        } else {
          // ok:false is HTTP 200 — a compile verdict, not a failed request. The diagnostics stay on
          // screen in the persistent list; the previous parameters stay too (marked stale).
          this.showSnack(`ตรวจสอบไม่ผ่าน — ผิดพลาด ${this.errorList.length} รายการ`, 'error')
        }
      } catch (err) {
        this.transportError = (err && err.message) || 'เรียกตรวจสอบไม่สำเร็จ'
      } finally {
        this.validating = false
      }
    },

    // ── run ──
    async doRun() {
      if (this.running) return
      this.bindingParam = ''
      this.transportError = ''
      let args
      try {
        args = this.buildArguments()
      } catch (err) {
        this.bindingParam = (err && err.parameter) || ''
        this.runResult = {
          ok: false,
          errorKind: 'binding',
          errorParameter: this.bindingParam,
          errorMessage: (err && err.message) || 'ค่านำเข้าไม่ถูกต้อง',
        }
        return
      }
      this.running = true
      const ranSource = this.editorSource()
      try {
        const res = await taxCalculateV2Api.runFunction(this.functionId, {
          sourceCode: ranSource,
          arguments: args,
        })
        this.lastRunSource = ranSource
        this.runResult = res || null
        if (res && res.ok) {
          this.setErrors([], '')
          this.showSnack(`รันสำเร็จ (${res.elapsedMs != null ? `${res.elapsedMs} ms` : 'สำเร็จ'})`, 'success')
        } else if (res && res.errorKind === 'compile') {
          this.setErrors(res.errors, 'ผลการรัน (คอมไพล์)')
        } else if (res && res.errorKind === 'binding') {
          this.bindingParam = res.errorParameter || ''
        }
      } catch (err) {
        this.transportError = (err && err.message) || 'รันฟังก์ชันไม่สำเร็จ'
      } finally {
        this.running = false
      }
    },

    // ── save ──
    /** Rewrite the class declaration to match รหัสฟังก์ชัน — the author's own click, never automatic. */
    syncClassName() {
      const fc = String(this.form.functionCode || '').trim()
      if (!fc || !FUNCTION_CODE_RE.test(fc)) return
      this.code = String(this.code || '').replace(CLASS_DECL_RE, `class ${fc}`)
      this.showSnack(`แก้ชื่อคลาสในโค้ดเป็น ${fc} แล้ว`, 'success')
    },
    async doSave() {
      if (!this.canSave) {
        this.showSnack(this.metaErrors[0] || 'ยังบันทึกไม่ได้', 'error')
        return
      }
      this.saving = true
      this.transportError = ''
      try {
        // sourceCode is ALWAYS sent from this page — the server-side "rename rewrites the class
        // declaration" shortcut only applies when it is absent, and here the author may have changed
        // both the code and the รหัสฟังก์ชัน. Send both and let the compile speak.
        const body = {
          functionCode: String(this.form.functionCode || '').trim(),
          name: String(this.form.name || '').trim(),
          description: this.form.description || '',
          // Trimmed HERE, not on every keystroke — the FieldKey is a verbatim runtime key, so the only
          // thing that may be corrected is the whitespace a paste dragged in with it. Never upper-cased.
          fieldKey: String(this.form.fieldKey || '').trim(),
          sourceCode: this.editorSource(),
          sortOrder: Number(this.form.sortOrder) || 0,
          isActive: this.form.isActive !== false,
          // Full replace, like every other field here — the complete list, orphans included.
          parameterBindings: this.buildParameterBindings(),
        }
        const saved = await taxCalculateV2Api.updateFunction(this.functionId, body)

        // A save is NOT rejected because the code does not compile — the backend records the verdict on
        // the row. So the response is READ BACK rather than treated as pass/fail, and whatever it says
        // about compileStatus / compileErrors is what the author is now looking at.
        if (saved && typeof saved === 'object') {
          this.applyFunction(saved, false)
        } else {
          this.savedForm = { ...this.form }
          this.savedSourceCode = this.code
          this.savedBindingsJson = this.bindingsSignature
          const refreshed = await taxCalculateV2Api.getFunction(this.functionId).catch(() => null)
          if (refreshed) this.applyFunction(refreshed, false)
        }
        this.discardDraft()

        // "Error, N problems" with nothing to read is the one outcome the author cannot act on — a
        // summary response carries the count but not the diagnostics, so fetch them.
        if (this.compileStatus === 'Error' && !this.errorList.length) {
          const full = await taxCalculateV2Api.getFunction(this.functionId).catch(() => null)
          if (full) this.applyFunction(full, false)
        }

        if (this.compileStatus === 'Error') {
          this.showSnack(`บันทึกแล้ว แต่คอมไพล์ไม่ผ่าน (${this.compileErrorCount} ข้อผิดพลาด) — ดูรายการด้านล่าง`, 'warning')
        } else if (this.isStale) {
          this.showSnack('บันทึกแล้ว — สถานะคอมไพล์ยังเป็นของโค้ดชุดก่อน ต้องคอมไพล์ใหม่', 'warning')
        } else {
          this.showSnack('บันทึกฟังก์ชันสำเร็จ', 'success')
        }
      } catch (err) {
        this.showSnack((err && err.message) || 'บันทึกไม่สำเร็จ', 'error')
      } finally {
        this.saving = false
      }
    },

    // ── nav ──
    listRoute() {
      return `/TaxCalculateV2/${this.configVersionId}/${this.calcVersionId}`
    },
    goBack() {
      this.$router.push(this.listRoute())
    },
    cancelLeave() {
      this.showLeaveDialog = false
      if (this.pendingRoute) { this.pendingRoute(false); this.pendingRoute = null }
    },
    confirmLeave() {
      this.showLeaveDialog = false
      // "ออกโดยไม่บันทึก" promises the edits are gone — so the local draft goes with them, and the
      // pending debounce is killed first so it cannot write one back on the way out.
      if (this.draftTimer) { clearTimeout(this.draftTimer); this.draftTimer = null }
      this.discardDraft()
      this.savedForm = { ...this.form }
      this.savedSourceCode = this.code
      this.savedBindingsJson = this.bindingsSignature
      if (this.pendingRoute) { const go = this.pendingRoute; this.pendingRoute = null; go() }
    },
    /** Pad this page below the app-shell's fixed breadcrumb bar so the toolbar isn't overlapped. */
    updateLayoutInsets() {
      const root = this.$refs.tcfPageRoot
      if (!root) return
      const naturalTop = root.getBoundingClientRect().top
      let breadcrumbBottom = naturalTop
      const bc = document.querySelector('.fixed-breadcrumbs')
      if (bc) {
        const r = bc.getBoundingClientRect()
        if (r.height > 0) breadcrumbBottom = r.bottom
      }
      this.topPad = Math.max(0, Math.round(breadcrumbBottom - naturalTop))
      this.pageHeight = Math.max(0, Math.round(window.innerHeight - naturalTop))
    },
    showSnack(message, color) {
      this.snackbar = { show: true, color: color || 'success', message }
    },
  },
}
</script>

<style scoped>
.tcf-page {
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
  background: rgb(var(--v-theme-background));
}
.tcf-title { font-weight: 600; }
.tcf-fc {
  background: rgba(255, 255, 255, 0.18);
  padding: 1px 8px;
  border-radius: 6px;
  font-size: 0.9rem;
  margin-left: 4px;
}
.tcf-sub { opacity: 0.85; font-weight: 400; font-size: 0.9rem; margin-left: 4px; }
.tcf-text-primary { color: rgb(var(--v-theme-primary)) !important; }

.tcf-center {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
}

.tcf-meta {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  background: rgb(var(--v-theme-surface));
  flex-wrap: wrap;
}
.tcf-meta-code { max-width: 240px; }
.tcf-meta-name { max-width: 240px; }
/* Wider than the plain text field it replaced — it now holds a searchable key AND a dropdown chevron. */
.tcf-meta-field { max-width: 250px; }
.tcf-meta-desc { flex: 1; min-width: 180px; }
.tcf-meta-order { max-width: 96px; }
.tcf-meta-active { flex: none; margin-top: -2px; }

.tcf-draft { margin: 10px 16px 0; }

.tcf-body {
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: stretch;
}
.tcf-editor-pane {
  flex: 1 1 60%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  padding: 10px 12px 10px 16px;
}
.tcf-run-pane {
  flex: 0 0 380px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  border-left: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  background: rgb(var(--v-theme-surface));
  padding: 10px 16px 10px 12px;
}
.tcf-run-body { flex: 1; min-height: 0; overflow-y: auto; padding-right: 2px; }

.tcf-pane-head {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 6px;
  min-height: 26px;
}
.tcf-pane-title { font-size: 0.88rem; font-weight: 600; }
.tcf-entry {
  margin-left: 8px;
  font-family: 'JetBrains Mono', 'Roboto Mono', monospace;
  font-size: 0.76rem;
  color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.1);
  padding: 0 6px;
  border-radius: 5px;
}
.tcf-hint {
  font-size: 0.74rem;
  color: rgba(var(--v-theme-on-surface), 0.55);
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.tcf-classfix {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  font-size: 0.76rem;
  color: rgb(var(--v-theme-warning));
  background: rgba(var(--v-theme-warning), 0.1);
  border: 1px solid rgba(var(--v-theme-warning), 0.35);
  border-radius: 8px;
  padding: 5px 10px;
  margin-bottom: 6px;
}
.tcf-classfix code {
  font-family: 'JetBrains Mono', 'Roboto Mono', monospace;
  background: rgba(var(--v-theme-on-surface), 0.08);
  padding: 0 5px;
  border-radius: 4px;
}

/* ===== Code editor =====
   Every colour below comes from the Vuetify theme, so the editor follows light/dark with the rest of
   the admin — the playground hard-coded Tailwind-slate hex and was legible in exactly one theme. */
.tcf-editor-shell {
  flex: 1;
  min-height: 220px;
  display: flex;
  overflow: hidden;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 10px;
  background: rgb(var(--v-theme-surface));
}
.tcf-editor {
  width: 100%;
  color: rgb(var(--v-theme-on-surface));
  font-family: 'JetBrains Mono', 'Roboto Mono', 'Consolas', monospace;
  font-size: 0.86rem;
  line-height: 1.65;
  padding: 10px 12px;
}
.tcf-editor-shell :deep(.prism-editor__textarea) {
  caret-color: rgb(var(--v-theme-primary));
}
.tcf-editor-shell :deep(.prism-editor__textarea:focus) {
  outline: none;
}
.tcf-editor-shell :deep(.prism-editor__textarea::selection) {
  background: rgba(var(--v-theme-primary), 0.32);
}

/* Prism tokens — scoped, so no other Prism editor in the app is touched. */
.tcf-editor-shell :deep(.token.comment),
.tcf-editor-shell :deep(.token.prolog),
.tcf-editor-shell :deep(.token.doctype),
.tcf-editor-shell :deep(.token.cdata) {
  color: rgba(var(--v-theme-on-surface), 0.45);
  font-style: italic;
}
.tcf-editor-shell :deep(.token.punctuation),
.tcf-editor-shell :deep(.token.operator) {
  color: rgba(var(--v-theme-on-surface), 0.72);
}
.tcf-editor-shell :deep(.token.keyword),
.tcf-editor-shell :deep(.token.boolean),
.tcf-editor-shell :deep(.token.preprocessor) {
  color: rgb(var(--v-theme-primary));
  font-weight: 600;
}
.tcf-editor-shell :deep(.token.string),
.tcf-editor-shell :deep(.token.char) {
  color: rgb(var(--v-theme-success));
}
.tcf-editor-shell :deep(.token.number) {
  color: rgb(var(--v-theme-warning));
}
.tcf-editor-shell :deep(.token.class-name),
.tcf-editor-shell :deep(.token.type-list),
.tcf-editor-shell :deep(.token.return-type),
.tcf-editor-shell :deep(.token.generic-method),
.tcf-editor-shell :deep(.token.function) {
  color: rgb(var(--v-theme-info));
}

/* Gutter. Its cells ship with `token comment` on them, so these must out-specify the token rules. */
.tcf-editor-shell :deep(.prism-editor__line-numbers) {
  padding-top: 0;
  border-right: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  padding-right: 8px;
}
.tcf-editor-shell :deep(.prism-editor__line-numbers .prism-editor__line-number) {
  color: rgba(var(--v-theme-on-surface), 0.38);
  font-style: normal;
}
.tcf-editor-shell :deep(.prism-editor__line-numbers .prism-editor__line-number.tcf-line-err) {
  color: rgb(var(--v-theme-error));
  font-weight: 700;
}
.tcf-editor-shell :deep(.prism-editor__line-numbers .prism-editor__line-number.tcf-line-hit) {
  color: rgb(var(--v-theme-error));
  font-weight: 700;
  background: rgba(var(--v-theme-error), 0.18);
  border-radius: 4px;
}

/* ===== Run panel ===== */
.tcf-params { display: flex; flex-direction: column; gap: 4px; }
.tcf-param-wrap { display: flex; flex-direction: column; }
.tcf-param { width: 100%; }
.tcf-param-bound {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  align-self: flex-start;
  font-size: 0.7rem;
  color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.1);
  border-radius: 5px;
  padding: 1px 6px;
  margin-bottom: 2px;
}
.tcf-param-bound code {
  font-family: 'JetBrains Mono', 'Roboto Mono', monospace;
  font-size: 0.7rem;
}
.tcf-empty {
  font-size: 0.8rem;
  color: rgba(var(--v-theme-on-surface), 0.55);
  display: flex;
  align-items: center;
  padding: 8px 0;
}
.tcf-result {
  margin-top: 12px;
  padding: 10px;
  border-radius: 10px;
  border: 1px dashed rgba(var(--v-theme-primary), 0.35);
  background: rgba(var(--v-theme-primary), 0.05);
}
.tcf-result-head { display: flex; align-items: center; font-size: 0.85rem; }
.tcf-elapsed { font-size: 0.74rem; color: rgba(var(--v-theme-on-surface), 0.55); font-variant-numeric: tabular-nums; }
.tcf-result-pre {
  margin: 6px 0 0;
  padding: 8px 10px;
  border-radius: 6px;
  background: rgba(var(--v-theme-on-surface), 0.06);
  color: rgb(var(--v-theme-on-surface));
  font-family: 'JetBrains Mono', 'Roboto Mono', monospace;
  font-size: 0.82rem;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 240px;
  overflow: auto;
}
.tcf-result-meta {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-top: 6px;
  font-size: 0.74rem;
  color: rgba(var(--v-theme-on-surface), 0.55);
}
.tcf-result-meta code { font-family: 'JetBrains Mono', 'Roboto Mono', monospace; }
.tcf-result-cached { font-style: italic; }
.tcf-run-sub { font-size: 0.8rem; margin-top: 4px; word-break: break-word; }
.tcf-detail-title { font-size: 0.8rem; min-height: 36px; }

/* ===== Parameter binding table ===== */
.tcf-bindings {
  flex: 0 0 auto;
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  background: rgb(var(--v-theme-surface));
  padding: 6px 16px 8px;
}
.tcf-bind-head {
  display: flex;
  align-items: center;
  gap: 2px;
  min-height: 34px;
  flex-wrap: wrap;
}
.tcf-bind-body {
  max-height: 34vh;
  overflow-y: auto;
  padding-top: 2px;
}
.tcf-bind-row {
  display: grid;
  grid-template-columns: minmax(150px, 220px) minmax(0, 1fr) auto;
  align-items: center;
  gap: 2px 12px;
  padding: 6px 8px;
  border-radius: 8px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  margin-bottom: 6px;
}
.tcf-bind-row-error {
  border-color: rgba(var(--v-theme-error), 0.45);
  background: rgba(var(--v-theme-error), 0.05);
}
.tcf-bind-row-orphan {
  background: rgba(var(--v-theme-on-surface), 0.05);
  border-style: dashed;
}
.tcf-bind-name { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
.tcf-bind-code {
  font-family: 'JetBrains Mono', 'Roboto Mono', monospace;
  font-size: 0.82rem;
  color: rgb(var(--v-theme-on-surface));
  word-break: break-all;
}
.tcf-bind-type { font-size: 0.72rem; color: rgba(var(--v-theme-on-surface), 0.55); }
.tcf-bind-input { min-width: 0; }
.tcf-bind-switch { display: flex; align-items: center; justify-content: flex-end; }
/* Trim the switch's stock control height so a row stays one line tall next to a compact input. */
.tcf-bind-switch :deep(.v-selection-control) { min-height: 30px; }
.tcf-bind-switch :deep(.v-label) { font-size: 0.78rem; opacity: 0.9; }
.tcf-bind-sel {
  font-family: 'JetBrains Mono', 'Roboto Mono', monospace;
  font-size: 0.8rem;
}
.tcf-bind-opt-label { font-size: 0.76rem; }
.tcf-bind-msg {
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 12px;
  font-size: 0.74rem;
  line-height: 1.5;
}
.tcf-bind-msg:empty { display: none; }
.tcf-bind-err { color: rgb(var(--v-theme-error)); display: inline-flex; align-items: center; gap: 3px; }
.tcf-bind-warn { color: rgb(var(--v-theme-warning)); display: inline-flex; align-items: center; gap: 3px; }
.tcf-bind-note { color: rgba(var(--v-theme-on-surface), 0.55); }
.tcf-bind-orphans { margin-top: 8px; }
.tcf-bind-orphan-head {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.74rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
  margin-bottom: 4px;
}
.tcf-bind-orphan-val { font-size: 0.78rem; color: rgba(var(--v-theme-on-surface), 0.7); }

/* ===== Persistent error list ===== */
.tcf-error-list {
  max-height: 132px;
  overflow-y: auto;
  padding: 6px 16px 10px;
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  background: rgb(var(--v-theme-surface));
}
.tcf-error-head {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.78rem;
  font-weight: 600;
  color: rgb(var(--v-theme-error));
  margin-bottom: 2px;
}
.tcf-error-src { font-weight: 400; color: rgba(var(--v-theme-on-surface), 0.55); }
.tcf-error-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.78rem;
  line-height: 1.7;
  color: rgb(var(--v-theme-error));
}
.tcf-error-click { cursor: pointer; border-radius: 4px; padding: 0 4px; }
.tcf-error-click:hover { background: rgba(var(--v-theme-error), 0.1); }
.tcf-error-active { background: rgba(var(--v-theme-error), 0.16); }

@media (max-width: 1100px) {
  .tcf-body { flex-direction: column; overflow-y: auto; }
  .tcf-editor-pane { flex: none; padding: 10px 16px 0; }
  .tcf-editor-shell { min-height: 320px; }
  .tcf-run-pane {
    flex: none;
    border-left: none;
    border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    padding: 10px 16px;
  }
  .tcf-run-body { overflow: visible; }
  .tcf-bind-row { grid-template-columns: 1fr; }
  .tcf-bind-switch { justify-content: flex-start; }
  .tcf-bind-body { max-height: none; }
}
</style>
